/**
 * Leader election across the windows of one browser, over `BroadcastChannel`.
 *
 * Five open windows must not run five copies of the notification poller: that
 * is five `SELECT` + `SEARCH` round trips per watched folder per minute against
 * the user's own mail host, for one person's mail (ADR 0021 "Costs accepted").
 * One window polls; the rest sit quiet and learn about arrivals through the
 * cache-patch channel they already share.
 *
 * ## The rule
 *
 * A **visible** window outranks a hidden one, because the visible window is the
 * one whose list and toasts the user is looking at, and it is the one the
 * browser is least likely to throttle. Ties break on a random per-window
 * number, which is enough: the election is not a consensus problem, and the
 * cost of two leaders for one round is one duplicate poll.
 *
 * ## Failure is bounded, not prevented
 *
 * A leader that is closed, crashed or frozen simply stops sending heartbeats,
 * and the first follower to notice the silence claims the role. There is no
 * lock to leak and no state to recover: the poller's watermark is per-leader,
 * and a new leader's first poll carries no `after`, which the server answers
 * with a watermark and no events — so a handover cannot raise a backlog of
 * toasts.
 */

export const LEADER_CHANNEL_NAME = 'ols-mail-leader';

/** How often the leader says it is alive. */
export const HEARTBEAT_MS = 2_000;
/** Silence longer than this means the leader is gone. */
export const LEADER_TIMEOUT_MS = 6_000;

interface ClaimMessage {
  type: 'claim' | 'heartbeat' | 'resign';
  id: string;
  /** 0 for a visible window, 1 for a hidden one. Lower wins. */
  rank: number;
  tiebreak: number;
}

function isClaim(value: unknown): value is ClaimMessage {
  if (typeof value !== 'object' || value === null) return false;
  const message = value as Partial<ClaimMessage>;
  return (
    (message.type === 'claim' || message.type === 'heartbeat' || message.type === 'resign') &&
    typeof message.id === 'string' &&
    typeof message.rank === 'number' &&
    typeof message.tiebreak === 'number'
  );
}

/** True when `a` should lead instead of `b`. */
function outranks(a: Pick<ClaimMessage, 'rank' | 'tiebreak' | 'id'>, b: Pick<ClaimMessage, 'rank' | 'tiebreak' | 'id'>): boolean {
  if (a.rank !== b.rank) return a.rank < b.rank;
  if (a.tiebreak !== b.tiebreak) return a.tiebreak > b.tiebreak;
  return a.id > b.id;
}

export interface LeaderElection {
  /** True while this window holds the role. */
  isLeader(): boolean;
  /** Re-runs the election — call it when visibility changes. */
  refresh(): void;
  stop(): void;
}

export interface LeaderElectionOptions {
  channelName?: string;
  onChange: (isLeader: boolean) => void;
  /** Injectable so a test does not depend on `document`. */
  isVisible?: () => boolean;
}

export function createLeaderElection(options: LeaderElectionOptions): LeaderElection {
  const isVisible =
    options.isVisible ??
    (() => typeof document === 'undefined' || document.visibilityState === 'visible');

  const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  const tiebreak = Math.random();
  let leader = false;
  let lastSeenLeaderAt = 0;
  let bestRival: ClaimMessage | undefined;
  let stopped = false;

  const channel =
    typeof BroadcastChannel === 'function' ? new BroadcastChannel(options.channelName ?? LEADER_CHANNEL_NAME) : undefined;

  const me = (): ClaimMessage => ({ type: 'claim', id, rank: isVisible() ? 0 : 1, tiebreak });

  const setLeader = (next: boolean): void => {
    if (leader === next) return;
    leader = next;
    options.onChange(next);
  };

  const post = (type: ClaimMessage['type']): void => {
    channel?.postMessage({ ...me(), type });
  };

  const evaluate = (): void => {
    if (stopped) return;
    // With no BroadcastChannel there is exactly one window we can know about,
    // and it is this one. Leading alone beats never polling.
    if (channel === undefined) {
      setLeader(true);
      return;
    }
    const rival = bestRival;
    const rivalIsAlive = rival !== undefined && Date.now() - lastSeenLeaderAt < LEADER_TIMEOUT_MS;
    if (rivalIsAlive && outranks(rival, me())) {
      setLeader(false);
      return;
    }
    bestRival = undefined;
    setLeader(true);
    post('heartbeat');
  };

  if (channel !== undefined) {
    channel.onmessage = (event: MessageEvent<unknown>) => {
      if (!isClaim(event.data) || event.data.id === id) return;
      const other = event.data;
      if (other.type === 'resign') {
        if (bestRival?.id === other.id) bestRival = undefined;
        evaluate();
        return;
      }
      if (bestRival === undefined || outranks(other, bestRival)) bestRival = other;
      lastSeenLeaderAt = Date.now();
      // A claim from a better-ranked window: step down without waiting for the
      // timeout, so a window the user just focused takes over immediately.
      if (outranks(other, me())) {
        setLeader(false);
        return;
      }
      if (other.type === 'claim') post('heartbeat');
    };
  }

  post('claim');
  const ticker = setInterval(evaluate, HEARTBEAT_MS);
  // Give rivals one round to answer before declaring victory in an empty room.
  const firstRound = setTimeout(evaluate, HEARTBEAT_MS / 4);

  return {
    isLeader: () => leader,
    refresh: () => {
      post('claim');
      evaluate();
    },
    stop: () => {
      stopped = true;
      clearInterval(ticker);
      clearTimeout(firstRound);
      if (leader) post('resign');
      setLeader(false);
      channel?.close();
    },
  };
}
